import { AfterViewInit, OnDestroy, OnInit, Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { SearchBarService } from '../../services/search.service';
import { FlightType, SearchType } from '../../../../shared/models/search-bar';

@Component({
  selector: 'app-booking-search-bar',
  standalone: false,
  templateUrl: './booking-search-bar.component.html',
  styleUrl: './booking-search-bar.component.scss'
})
export class BookingSearchBarComponent implements AfterViewInit, OnDestroy , OnInit {
  private readonly hashChangeHandler = () => this.setTabFromHash();
  // Compact summary on non-home routes
  isHomeRoute = true;
  showFullForm = true;
  ngOnInit() {
    this.setTabFromHash();
    window.addEventListener('hashchange', this.hashChangeHandler);
    this.evaluateHomeRoute();
    this.showFullForm = this.isHomeRoute; // only compact outside home
  }
  ngAfterViewInit(): void {
    this.setTabFromHash();
    window.addEventListener('hashchange', this.hashChangeHandler);
  }

  ngOnDestroy(): void {
    window.removeEventListener('hashchange', this.hashChangeHandler);
  }

  private setTabFromHash() {
    const hash = window.location.hash.toLowerCase();
    if (hash === '#flight') {
      this.setSearchType(this.searchTypeEnum.FLIGHT);
    } else if (hash === '#hotels') {
      this.setSearchType(this.searchTypeEnum.HOTEL);
    } else if (hash === '#cars') {
      this.setSearchType(this.searchTypeEnum.CAR);
    }
  }

  @Input() classes: string[] = [];
  @Input() showHeader: boolean = true;
  @Input() set defaultSearchType(type: SearchType) {
    this.searchBarService.searchType.next(type);
  }
  toreset=true;
  bsValue=new Date();
  time: Date | null = null;

  get searchTypeEnum(): typeof SearchType {
    return SearchType;
  }

  constructor(
    private readonly router: Router,
    private readonly searchBarService: SearchBarService,
  ) {
    const defaultTime = new Date();
    defaultTime.setHours(10, 30, 0, 0); // Set hours, minutes, seconds, milliseconds
    this.time = defaultTime;
  }

  search(searchString: string = '') {
    const selection = this.searchBarService.searchType.value
    if(selection === SearchType.FLIGHT) {
      this.router.navigateByUrl('/flight/flight-list?' + searchString);
    } else if(selection === SearchType.HOTEL) {
      this.router.navigateByUrl('/hotel/hotel-list?' + searchString);
    } else if(selection === SearchType.CAR) {
      this.router.navigateByUrl('/car/car-list?' + searchString);
    }
    // After triggering a search, collapse the form on non-home routes
    if (!this.isHomeRoute) {
      this.showFullForm = false;
    }
  }

  setSearchType(type: SearchType) {
    this.searchBarService.searchType.next(type);
  }

  toggleFormVisibility(): void {
    this.showFullForm = !this.showFullForm;
  }

  private evaluateHomeRoute(): void {
    const url = this.router.url || '';
    // Treat root and any '/home*' as home pages
    this.isHomeRoute = url === '/' || url.includes('home');
  }

  // Summary helpers
  get isCompact(): boolean { return !this.isHomeRoute && !this.showFullForm; }
  get currentType(): SearchType { return this.searchBarService.searchType.value; }

  get summaryTitle(): string {
    if (this.currentType === SearchType.FLIGHT) {
      const req: any = this.searchBarService.flightSearchRequest;
      const od = req?.OriginDestinationInfo?.[0];
      const from = od?.airportOriginCode || 'From';
      const to = od?.airportDestinationCode || 'To';
      return `${from} → ${to}`;
    }
    if (this.currentType === SearchType.HOTEL) {
      const req: any = this.searchBarService.hotelSearchRequest;
      return req?.city_name || 'Select city';
    }
    // CAR
    const req: any = this.searchBarService.carSearchRequest;
    const pickup = req?.pickup_id || 'Pickup';
    const drop = req?.dropoff_id || 'Dropoff';
    return `${pickup} → ${drop}`;
  }

  get summarySubtitle(): string {
    if (this.currentType === SearchType.FLIGHT) {
      const req: any = this.searchBarService.flightSearchRequest;
      const od = req?.OriginDestinationInfo?.[0] || {};
      const date = this.formatDateShort(od?.departureDate);
      const roundDate = this.formatDateShort(od?.returnDate);
      const total = this.totalTravelers(req);
      const cls = this.flightClassToText(req?.class);
      let travelersText = '';
      if (total) {
        travelersText = `${total} traveler${total > 1 ? 's' : ''}`;
      }
      if(req?.journeyType==="Return" && roundDate){
      return [date,roundDate, travelersText, cls].filter(Boolean).join(' · ');
      }
      return [date, travelersText, cls].filter(Boolean).join(' · ');
    }
    if (this.currentType === SearchType.HOTEL) {
      const req: any = this.searchBarService.hotelSearchRequest;
      const inD = this.formatDateShort(req?.checkin);
      const outD = this.formatDateShort(req?.checkout);
      const guests = this.hotelGuestsText(req);
      return [inD && outD ? `${inD} - ${outD}` : '', guests].filter(Boolean).join(' · ');
    }
    const req: any = this.searchBarService.carSearchRequest;
    const date = this.formatDateShort(req?.pick_up_date_time);
    return date || '';
  }

  private formatDateShort(d: any): string {
    try {
      let date: Date | null = null;
      if (typeof d === 'string') {
        date = new Date(d);
      } else if (d instanceof Date) {
        date = d;
      }
      if (!date || isNaN(date.getTime())) return '';
      return date.toLocaleDateString(undefined, { month: 'short', day: '2-digit' });
    } catch { return ''; }
  }

  private totalTravelers(req: any): number {
    const a = parseInt(req?.adults ?? 0, 10) || 0;
    const c = parseInt(req?.childs ?? 0, 10) || 0;
    const i = parseInt(req?.infants ?? 0, 10) || 0;
    return a + c + i;
  }

  private flightClassToText(cls: FlightType): string {
    switch (cls) {
      case FlightType.FIRST_CLASS: return 'First Class';
      case FlightType.BUSINESS: return 'Business';
      case FlightType.PREMIUM_ECONOMY: return 'Premium Economy';
      case FlightType.ECONOMY:
      default: return 'Economy';
    }
  }

  private hotelGuestsText(req: any): string {
    const room = req?.roomAndGuest;
    if (!room) return '';
    const rooms = room.roomCount || 1;
    const adults = room.adultCount || 1;
    const children = room.childCount || 0;
    const total = (parseInt(adults,10)||0) + (parseInt(children,10)||0);
    return `${total} guest${total>1?'s':''} · ${rooms} room${rooms>1?'s':''}`;
  }

  get searchType(): SearchType {
    return this.searchBarService.searchType.value;
  }



}
